import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon, ArrowRightIcon, ExclamationTriangleIcon,
  XMarkIcon, BoltIcon, CalendarIcon, ArrowTrendingUpIcon,
  CurrencyDollarIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import apiClient from '../../lib/apiClient';

interface DashboardStats {
  total_users: number;
  total_searches: number;
  completed_searches: number;
  new_users_this_month: number;
  user_growth_rate: number;
  total_credits_in_system: number;
  total_credits_used: number;
  revenue_this_month: number;
  revenue_last_month: number;
  revenue_growth: number;
  total_revenue: number;
  open_tickets: number;
  today_searches: number;
  today_completed_searches: number;
  today_credits_spent: number;
  today_payments: number;
  today_new_users: number;
  total_companies_searched: number;
  companies_enriched: number;
  total_people_found: number;
  active_jobs: number;
  failed_jobs: number;
  paying_customers: number;
  active_plans: number;
  active_coupons: number;
  cached_rows_total: number;
  arpu: number;
  recent_searches: any[];
  recent_activity: any[];
  recent_tickets: any[];
  recent_enquiries: any[];
  openrouter_balance: number | null;
  serper_balance: number;
  daily_search_avg: number;
  api_cost: number;
  net_profit: number;
}

function getInitials(name: string) {
  return (name || 'U').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
}
function getAvatarBg(id: string | number) {
  const colors = ['bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700'];
  return colors[String(id).charCodeAt(0) % colors.length];
}
function statusColor(status: string) {
  const m: Record<string, string> = { completed: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-600', processing: 'bg-brand-100 text-brand-700', pending: 'bg-slate-100 text-slate-600', retrieving: 'bg-blue-100 text-blue-700' };
  return m[status] ?? 'bg-slate-100 text-slate-600';
}
function ticketPriorityColor(priority: string) {
  const m: Record<string, string> = { urgent: 'bg-red-100 text-red-700', high: 'bg-amber-100 text-amber-700', medium: 'bg-blue-100 text-blue-700', low: 'bg-slate-100 text-slate-600' };
  return m[priority] ?? 'bg-slate-100 text-slate-600';
}

function Sparkline({ values, color = 'bg-brand-500' }: { values: number[]; color?: string }) {
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-0.5 h-8">
      {values.map((v, i) => (
        <div key={i} className={`flex-1 rounded-t-sm ${i === values.length - 1 ? color : color.replace('500', '200')}`}
          style={{ height: `${Math.max(10, Math.round((v / max) * 100))}%` }} />
      ))}
    </div>
  );
}

function Donut({ pct, color }: { pct: number; color: string }) {
  const r = 28; const circ = 2 * Math.PI * r; const dash = (Math.min(pct, 100) / 100) * circ;
  return (
    <svg width="72" height="72" viewBox="0 0 72 72">
      <circle cx="36" cy="36" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
      <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="7"
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" transform="rotate(-90 36 36)" />
      <text x="36" y="40" textAnchor="middle" fontSize="12" fontWeight="800" fill="#0f172a">{pct}%</text>
    </svg>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertDismissed, setAlertDismissed] = useState(false);

  useEffect(() => {
    apiClient.get('/admin/dashboard-stats')
      .then(({ data }) => { setStats(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const completionRate = stats && stats.total_searches > 0
    ? Math.round((stats.completed_searches / stats.total_searches) * 100) : 0;

  const conversionPct = stats
    ? Math.min(25, Math.round((stats.new_users_this_month / Math.max(stats.total_users, 1)) * 100 * 4)) : 14;

  return (
    <div className="flex flex-col gap-0 pb-16">

      {/* Alert Banner */}
      {!alertDismissed && stats && stats.open_tickets > 0 && (
        <div className="mx-6 mt-6 bg-red-50 border border-red-200 rounded-2xl px-5 py-3.5 flex items-center gap-4">
          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
            <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-extrabold text-red-800">Support Alert — {stats.open_tickets} Open Tickets</p>
            <p className="text-[12px] text-red-600 font-medium">There are {stats.open_tickets} unresolved support tickets requiring attention.</p>
          </div>
          <Link to="/support-tickets" className="bg-red-700 text-white text-[12px] font-bold px-4 py-1.5 rounded-lg hover:bg-red-800 transition-colors shrink-0">
            View Tickets
          </Link>
          <button onClick={() => setAlertDismissed(true)} className="text-red-300 hover:text-red-600">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto w-full flex flex-col gap-6">

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Total Users */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Total Users</p>
            {loading ? <div className="h-10 w-24 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{stats?.total_users.toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">+{stats?.new_users_this_month} this month</p>}
          </div>

          {/* Today Users */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-blue-600 uppercase tracking-widest mb-3">Today Registrations</p>
            {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{stats?.today_new_users ?? 0}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">New users</p>}
          </div>

          {/* Total Searches */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Total Searches</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{stats?.total_searches.toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">{stats?.completed_searches.toLocaleString()} completed</p>}
          </div>

          {/* Today Searches */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-brand-600 uppercase tracking-widest mb-3">Today Searches</p>
            {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{stats?.today_searches ?? 0}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">{stats?.today_completed_searches ?? 0} completed</p>}
          </div>

          {/* Total Revenue */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-3">Total Revenue</p>
            {loading ? <div className="h-10 w-24 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">${(stats?.total_revenue ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">${(stats?.revenue_this_month ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} this month</p>}
          </div>

          {/* Today Payments */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-3">Today Revenue</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">${(stats?.today_payments ?? 0).toFixed(0)}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Today payments</p>}
          </div>

          {/* Total Credits System */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Credits in System</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.total_credits_in_system ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Granted to users</p>}
          </div>

          {/* Total Credits Used */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Credits Used</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.total_credits_used ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Total consumed</p>}
          </div>

          {/* Today Credits Spent */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-brand-600 uppercase tracking-widest mb-3">Today Credits Used</p>
            {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.today_credits_spent ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Consumed today</p>}
          </div>

          {/* Total Companies Searched */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Companies Searched</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.total_companies_searched ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Total companies</p>}
          </div>

          {/* Companies Enriched */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-purple-600 uppercase tracking-widest mb-3">Companies Enriched</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.companies_enriched ?? 0).toLocaleString()}</p>}
            {!loading && stats && <p className="text-[11px] text-slate-400 mt-2">{Math.round((stats.companies_enriched / Math.max(stats.total_companies_searched, 1)) * 100)}% success rate</p>}
          </div>

          {/* People Found */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-purple-600 uppercase tracking-widest mb-3">People Found</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.total_people_found ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Decision makers extracted</p>}
          </div>

          {/* Paying Customers */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-3">Paying Customers</p>
            {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.paying_customers ?? 0).toLocaleString()}</p>}
            {!loading && stats && <p className="text-[11px] text-slate-400 mt-2">{Math.round((stats.paying_customers / Math.max(stats.total_users, 1)) * 100)}% of users</p>}
          </div>

          {/* ARPU */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-3">Avg Revenue / Customer</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">${(stats?.arpu ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Per paying customer</p>}
          </div>

          {/* Active Jobs */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-brand-600 uppercase tracking-widest mb-3">Active Jobs</p>
            {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.active_jobs ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Running / queued</p>}
          </div>

          {/* Failed Jobs */}
          <div className={`bg-white rounded-2xl p-3 sm:p-6 shadow-sm border ${(stats?.failed_jobs ?? 0) > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
            <p className="text-[10px] font-extrabold text-red-600 uppercase tracking-widest mb-3">Failed Jobs</p>
            {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.failed_jobs ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">All time</p>}
          </div>

          {/* Reused From Cache */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-blue-600 uppercase tracking-widest mb-3">Reused (Cache)</p>
            {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.cached_rows_total ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">Rows free (30-day)</p>}
          </div>

          {/* Active Plans */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Active Plans</p>
            {loading ? <div className="h-10 w-12 bg-slate-100 animate-pulse rounded" /> :
              <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.active_plans ?? 0).toLocaleString()}</p>}
            {!loading && <p className="text-[11px] text-slate-400 mt-2">{(stats?.active_coupons ?? 0).toLocaleString()} active coupons</p>}
          </div>
        </div>

        {/* API Health & Profit Section */}
        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-4">API Health & Profit</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* OpenRouter Credits */}
            <div className={`bg-white rounded-2xl p-3 sm:p-6 shadow-sm border ${(stats?.openrouter_balance ?? 0) < 5 ? 'border-orange-200 bg-orange-50' : 'border-slate-200'}`}>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">OpenRouter Credits</p>
              {loading ? <div className="h-10 w-24 bg-slate-100 animate-pulse rounded" /> :
                <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{stats?.openrouter_balance ? stats.openrouter_balance.toFixed(1) : 'N/A'}</p>}
              {!loading && (stats?.openrouter_balance ?? 0) < 5 && <p className="text-[11px] text-orange-600 mt-2 font-semibold">⚠ Low balance</p>}
            </div>

            {/* Serper Credits */}
            <div className={`bg-white rounded-2xl p-3 sm:p-6 shadow-sm border ${(stats?.serper_balance ?? 0) < 1000 ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Serper Credits</p>
              {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
                <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{(stats?.serper_balance ?? 0).toLocaleString()}</p>}
              {!loading && (stats?.serper_balance ?? 0) < 1000 && <p className="text-[11px] text-red-600 mt-2 font-semibold">⚠ Low balance</p>}
            </div>

            {/* Recharge Estimate */}
            <div className={`bg-white rounded-2xl p-3 sm:p-6 shadow-sm border ${(stats?.daily_search_avg ?? 0) > 0 && Math.round((stats?.serper_balance ?? 0) / (stats?.daily_search_avg ?? 1)) < 7 ? 'border-amber-200 bg-amber-50' : 'border-slate-200'}`}>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Recharge In</p>
              {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
                <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">{stats?.daily_search_avg && stats?.serper_balance ? Math.round((stats.serper_balance) / stats.daily_search_avg) : '—'}</p>}
              {!loading && <p className="text-[11px] text-slate-400 mt-2">days remaining</p>}
            </div>

            {/* API Cost */}
            <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">API Cost (Month)</p>
              {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
                <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">$0</p>}
              {!loading && <p className="text-[11px] text-slate-400 mt-2">OpenRouter + Serper</p>}
            </div>

            {/* Net Profit */}
            <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
              <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-3">Net Profit</p>
              {loading ? <div className="h-10 w-20 bg-slate-100 animate-pulse rounded" /> :
                <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">$0</p>}
              {!loading && <p className="text-[11px] text-slate-400 mt-2">Revenue − API Cost</p>}
            </div>

            {/* Credits Revenue */}
            <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-6 shadow-sm">
              <p className="text-[10px] font-extrabold text-blue-600 uppercase tracking-widest mb-3">Credits Revenue</p>
              {loading ? <div className="h-10 w-16 bg-slate-100 animate-pulse rounded" /> :
                <p className="text-[22px] sm:text-[28px] xl:text-[32px] font-extrabold text-slate-900 leading-none break-all">$0</p>}
              {!loading && <p className="text-[11px] text-slate-400 mt-2">From credit sales</p>}
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 gap-6">

            {/* User Activity Monitor */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <p className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-500">User Activity Monitor</p>
                <Link to="/searches" className="text-[12px] font-bold text-brand-700 hover:text-slate-700 flex items-center gap-1">
                  View all <ArrowRightIcon className="w-3 h-3" />
                </Link>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[560px]">
                <thead>
                  <tr className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-50 bg-slate-50/40">
                    <th className="px-6 py-3">User</th>
                    <th className="px-6 py-3">Search</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Credits</th>
                    <th className="px-6 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    [1,2,3,4,5].map(i => (
                      <tr key={i}>{[1,2,3,4,5].map(j => (
                        <td key={j} className="px-6 py-4"><div className="h-4 bg-slate-100 animate-pulse rounded" style={{width:`${35+(j%4)*15}%`}} /></td>
                      ))}</tr>
                    ))
                  ) : (stats?.recent_activity ?? []).length === 0 ? (
                    <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400 text-sm">No recent activity.</td></tr>
                  ) : (stats?.recent_activity ?? []).map((s: any) => (
                    <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${getAvatarBg(s.user?.id ?? 0)}`}>
                            {getInitials(s.user?.name ?? 'U')}
                          </div>
                          <div>
                            <p className="text-[12px] font-bold text-slate-900 leading-tight">{s.user?.name ?? '—'}</p>
                            <p className="text-[10px] text-slate-400">{s.user?.email ?? ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3.5 text-[13px] font-bold text-slate-900">{s.company}</td>
                      <td className="px-6 py-3.5">
                        <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded ${statusColor(s.status)}`}>{s.status}</span>
                      </td>
                      <td className="px-6 py-3.5 text-[13px] font-bold text-slate-700">{s.credits_used}</td>
                      <td className="px-6 py-3.5 text-[12px] text-slate-400">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {/* Critical Support Tickets */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-[14px] font-extrabold text-slate-900">Critical Support Tickets</h3>
                {!loading && stats && (
                  <span className="bg-red-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full">
                    {stats.open_tickets} Active
                  </span>
                )}
              </div>
              <div className="divide-y divide-slate-50">
                {loading ? (
                  [1,2].map(i => (
                    <div key={i} className="px-6 py-4 flex gap-4">
                      <div className="w-8 h-8 bg-slate-100 animate-pulse rounded-xl shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-100 animate-pulse rounded w-3/4" />
                        <div className="h-3 bg-slate-50 animate-pulse rounded w-full" />
                      </div>
                    </div>
                  ))
                ) : (stats?.recent_tickets ?? []).length === 0 ? (
                  <div className="px-6 py-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-400" /> No open tickets — all clear!
                  </div>
                ) : (stats?.recent_tickets ?? []).map((t: any) => (
                  <div key={t.id} className="px-6 py-4 flex items-start gap-4 hover:bg-slate-50/60 transition-colors">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${ticketPriorityColor(t.priority).replace('text-', 'bg-').split(' ')[0].replace('bg-', 'bg-').replace('700', '50').replace('100', '50')}`}>
                      <ExclamationTriangleIcon className={`w-4 h-4 ${t.priority === 'urgent' ? 'text-red-600' : t.priority === 'high' ? 'text-amber-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-[13px] font-extrabold text-slate-900 truncate">{t.subject}</p>
                        <span className="text-[11px] text-slate-400 shrink-0">{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-[12px] text-slate-500 mb-2 truncate">{t.description}</p>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-full ${ticketPriorityColor(t.priority)}`}>{t.priority}</span>
                        <span className="text-[11px] text-slate-400">{t.user_name ?? t.user?.name ?? 'Anonymous'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 border-t border-slate-50 bg-slate-50/30">
                <Link to="/support-tickets" className="text-[12px] font-extrabold text-brand-600 hover:text-slate-700 flex items-center gap-1">
                  View all tickets <ArrowRightIcon className="w-3 h-3" />
                </Link>
              </div>
            </div>
        </div>

        {/* Recent Searches Table */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <p className="text-[13px] font-extrabold text-slate-900">Recent Searches</p>
            <Link to="/searches" className="text-[12px] font-bold text-brand-700 hover:text-slate-700 flex items-center gap-1">
              View all <ArrowRightIcon className="w-3 h-3" />
            </Link>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[560px]">
            <thead>
              <tr className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-50 bg-slate-50/40">
                <th className="px-6 py-3">Company</th><th className="px-6 py-3">User</th>
                <th className="px-6 py-3">Type</th><th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Credits</th><th className="px-6 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                Array.from({length:5}).map((_,i) => (
                  <tr key={i}>{[1,2,3,4,5,6].map(j => (
                    <td key={j} className="px-6 py-3.5"><div className="h-4 bg-slate-100 animate-pulse rounded" style={{width:`${40+(j%3)*20}%`}} /></td>
                  ))}</tr>
                ))
              ) : (stats?.recent_searches ?? []).map((s: any) => (
                <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-6 py-3.5 text-[13px] font-bold text-slate-900">{s.company}</td>
                  <td className="px-6 py-3.5">
                    <p className="text-[13px] font-medium text-slate-700">{s.user?.name ?? '—'}</p>
                    <p className="text-[11px] text-slate-400">{s.user?.email ?? ''}</p>
                  </td>
                  <td className="px-6 py-3.5 text-[12px] text-slate-500 capitalize">{s.type}</td>
                  <td className="px-6 py-3.5">
                    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded ${statusColor(s.status)}`}>{s.status}</span>
                  </td>
                  <td className="px-6 py-3.5 text-[13px] font-bold text-slate-700">{s.credits_used}</td>
                  <td className="px-6 py-3.5 text-[12px] text-slate-400">{new Date(s.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

      </div>
    </div>
  );
}
